import type { AnalyticsAttributesModel } from '~/common/models/store.index.models.ts';
import type { ApiErrorModel } from '~/common/models/api.models.ts';
import type { ParsedErrorModel, PaymentErrorModel, QuizTrackModel } from '~/common/models/mixpanel.model.ts';
import { withSubscriptionService } from '../utils/paymentEventProps';

function parseHtmlError(error: ApiErrorModel | string): { message: string } | null {
  const html = typeof error === 'string'
    ? error
    : Object.keys(error || {})
      .filter(key => /^\d+$/.test(key))
      .sort((a, b) => Number(a) - Number(b))
      .map(key => String(error[key as keyof typeof error]))
      .join('');

  if (!html.trim().startsWith('<')) {
    return null;
  }

  const message = extractTagContent(html, 'title')
    || extractTagContent(html, 'h1')
    || stripHtmlTags(html);

  return {
    message,
  };
}

function extractTagContent(html: string, tagName: string): string | null {
  const lowerHtml = html.toLowerCase();
  const openingTag = `<${tagName}`;
  const openingTagIndex = lowerHtml.indexOf(openingTag);

  if (openingTagIndex === -1) {
    return null;
  }

  const contentStart = lowerHtml.indexOf('>', openingTagIndex);
  if (contentStart === -1) {
    return null;
  }

  const closingTag = `</${tagName}>`;
  const closingTagIndex = lowerHtml.indexOf(closingTag, contentStart + 1);
  if (closingTagIndex === -1) {
    return null;
  }

  return html.slice(contentStart + 1, closingTagIndex).trim() || null;
}

function appendWhitespace(result: string, previousWasWhitespace: boolean): {
  result: string,
  previousWasWhitespace: boolean
} {
  if (previousWasWhitespace || !result) {
    return { result, previousWasWhitespace };
  }

  return {
    result: result + ' ',
    previousWasWhitespace: true,
  };
}

function stripHtmlTags(html: string): string {
  let result = '';
  let isInsideTag = false;
  let previousWasWhitespace = false;

  for (const char of html) {
    if (char === '<') {
      isInsideTag = true;
      ({ result, previousWasWhitespace } = appendWhitespace(result, previousWasWhitespace));
      continue;
    }

    if (char === '>') {
      isInsideTag = false;
      continue;
    }

    if (isInsideTag) {
      continue;
    }

    if (/\s/.test(char)) {
      ({ result, previousWasWhitespace } = appendWhitespace(result, previousWasWhitespace));
      continue;
    }

    result += char;
    previousWasWhitespace = false;
  }

  return result.trim();
}

function normalizeMagicWordMessage(message: string): { message: string } {
  const magicWord = 'Error message:';

  if (typeof message !== 'string' || !message.includes(magicWord)) {
    return { message };
  }

  const [_, partTwo] = message.split(magicWord);
  return { message: partTwo ? magicWord + partTwo : message };
}

function parseDisplayMessage(displayMessage: string, statusCode?: number) {
  const normalizedMessage = normalizeMagicWordMessage(displayMessage);

  return normalizedMessage.message === displayMessage
    ? { ...normalizedMessage, statusCode }
    : normalizedMessage;
}

function getPostHog() {
  return usePostHog();
}

function parseJSON(string: string) {
  try {
    return JSON.parse(string);
  } catch {
    return parseHtmlError(string) || { message: string };
  }
}

// Chargebee кладёт текст ошибки в `message`, Lago — в `error_details.thirdparty_error`
// (текст от Stripe) либо в `error`. Без этого message остаётся undefined и весь
// парсинг падает, унося с собой и событие Payment Error, и попап с ошибкой.
function extractDetailMessage(parsedError: any): string {
  return parsedError?.message
    ?? parsedError?.error_details?.thirdparty_error
    ?? parsedError?.error
    ?? '';
}

function parseDetailedError(detailError: string) {
  const parsedError = parseJSON(detailError);
  const message = extractDetailMessage(parsedError);
  const normalizedMessage = normalizeMagicWordMessage(message);

  return normalizedMessage.message === message
    ? { ...parsedError, message }
    : normalizedMessage;
}

export function parseError(error: ApiErrorModel | string) {
  const htmlError = parseHtmlError(error);
  if (htmlError) {
    return htmlError;
  }

  if (typeof error === 'string') {
    return normalizeMagicWordMessage(error);
  }

  if (error?.statusMessage) {
    return parseDisplayMessage(error.statusMessage, error?.statusCode);
  }

  if (error?.displayMessage) {
    return parseDisplayMessage(error.displayMessage, error?.statusCode);
  }

  if (error?.detail?.error) {
    return parseDetailedError(error.detail.error);
  }

  if (error?.detail?.message) {
    return parseError(error.detail?.message);
  }

  if (error?.response?.data?.detail?.error) {
    return parseJSON(error?.response?.data?.detail?.error);
  }

  return { message: error?.message || error };
}

export function useMixpanel() {
  const route = useRoute();
  const store = useKegelAnalyticStore();
  const gatewayStore = useKegelGatewayStore();
  const dayjs = useDayjs();
  const { gatewayType, paypalGatewayType } = useHelpers();
  const { $versions, $mixpanel, $paymentMethods, $releaseVersion, $logger } = useNuxtApp();

  function mixpanelPaymentError({ stage, type, error, errorCode, declineCode }: PaymentErrorModel): ParsedErrorModel {
    let gateway = gatewayType.value;

    if (type === $paymentMethods.paypal) {
      gateway = paypalGatewayType.value;
    }

    try {
      const parsedError = parseError(error);
      delete parsedError.error_msg;
      delete parsedError.http_status_code;

      if (errorCode) {
        parsedError.error_code = errorCode;
      }

      if (declineCode) {
        parsedError.decline_code = declineCode;
      }

      const props = {
        ...parsedError,
        type,
        stage,
        gateway,
      };

      mixpanelTrack(
        'Payment Error',
        withSubscriptionService('Payment Error', props, gatewayStore.subscriptionService),
      );

      return props;
    } catch (trackingError: unknown) {
      $logger.note({
        category: 'analytics',
        message: 'parsing the payment error failed',
        level: 'warning',
        cause: trackingError,
      });

      return {
        message: typeof error === 'string' ? error : '',
        ...(errorCode ? { error_code: errorCode } : {}),
        type,
        stage,
        gateway,
      };
    }
  }

  function setUserProperty(properties: { [key: string]: any }): void {
    $mixpanel?.people.set(properties);
    getPostHog()?.setPersonProperties(properties);
  }

  function mixpanelQuizTrack(eventName: string, payload?: QuizTrackModel | AnalyticsAttributesModel): void {
    if ($versions?.value.trackOnlyAfterQuiz !== '0') {
      $mixpanel?.track(eventName, payload);
    }

    getPostHog()?.capture(eventName, payload);
  }

  function mixpanelTrack(eventName: string, payload?: any): void {
    $mixpanel?.track(eventName, payload);
    getPostHog()?.capture(eventName, payload);
  }

  async function getAnalyticsAttributes(clickId?: string): Promise<AnalyticsAttributesModel> {
    const savedAttributes = store.analyticsAttributions;
    const cmpId = store.cmpId;
    const clickTime = store.clickTime * 1000;

    return {
      'redtrack_click_id': clickId,
      'click_time': clickTime ? dayjs(clickTime).utc().format('YYYY-MM-DDTHH:mm:ss[Z]') : null,
      'fbp': gatewayStore.fbp || savedAttributes?.fbp,
      'redtrack_campaign_id': route.query?.cmpid as string || cmpId,
      'campaign': route.query?.sub3 as string || savedAttributes?.campaign,
      'adset': route.query?.sub2 as string || savedAttributes?.adset,
      'ad': route.query?.sub1 as string || savedAttributes?.ad,
      'ad_name': route.query?.sub4 as string || savedAttributes?.ad_name,
      'adset_name': route.query?.sub5 as string || savedAttributes?.adset_name,
      'campaign_name': route.query?.sub6 as string || savedAttributes?.campaign_name,
      'ad_placement': route.query?.sub7 as string || savedAttributes?.ad_placement,
      'project': 'Vue3',
      'fbclid': route.query?.fbclid || savedAttributes?.fbclid,
      'Release version': $releaseVersion,
      'utm_source': route.query.utm_source as string,
    };
  }

  async function sendAttribution(
    clickId: string,
  ): Promise<boolean> {
    const savedClickId = store.savedClickId;
    const attributesWasSet = store.attributesWasSet;
    const attributes = await getAnalyticsAttributes(clickId);

    if (clickId !== savedClickId || attributesWasSet !== true) {
      setUserProperty(attributes);

      if (!route.query.attribution) {
        mixpanelTrack('Attribution', attributes as AnalyticsAttributesModel);
      }
      store.savedClickId = clickId || '';
      store.attributesWasSet = true;
    }

    store.analyticsAttributions = attributes;

    return true;
  }

  return {
    sendAttribution,
    mixpanelPaymentError,
    mixpanelQuizTrack,
    mixpanelTrack,
    getAnalyticsAttributes,
    setUserProperty,
  };
}
